import { UserFinancialConnection } from "@common/dto/user/user-integrations-response.dto";
import { INTEGRATION_CONNECTIONS_SETTINGS } from "@common/helpers/integrationConnection";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { accountMatches } from "@components/pageHeader/filters/AggregatedFilters/AccountFilter/accountFilterToggle";

const includesTerm = (value: string | null | undefined, term: string) =>
    !!value && value.toLowerCase().includes(term);

const accountMatchesTerm = (account: IntegrationAccount, term: string) => {
    const financialAccount = account.financialAccount;

    if (!financialAccount) {
        return false;
    }

    return (
        accountMatches(financialAccount, term) ||
        [financialAccount.entity?.name, financialAccount.institutionName].some(
            (value) => includesTerm(value, term),
        )
    );
};

export const filterFinancialConnections = (
    connections: UserFinancialConnection[],
    search: string,
): UserFinancialConnection[] => {
    const term = search.toLowerCase();

    return connections.flatMap((userConnection) => {
        const { connection } = userConnection;

        const isBankNameMatch =
            "bankName" in connection &&
            typeof connection.bankName === "string" &&
            includesTerm(connection.bankName, term);

        if (isBankNameMatch) {
            return [userConnection];
        }

        const accounts = connection.accounts.filter((account) =>
            accountMatchesTerm(account, term),
        );

        return accounts.length > 0
            ? [{ ...userConnection, connection: { ...connection, accounts } }]
            : [];
    });
};

export const filterIntegrationConnections = (
    connections: IntegrationConnection[],
    search: string,
): IntegrationConnection[] => {
    const term = search.toLowerCase();

    return connections.flatMap((connection) => {
        const isConnectionMatch = [
            connection.label,
            connection.institutionName,
            INTEGRATION_CONNECTIONS_SETTINGS[connection.provider].label,
        ].some((value) => includesTerm(value, term));

        if (isConnectionMatch) {
            return [connection];
        }

        const integrationAccounts = connection.integrationAccounts?.filter(
            (account) => accountMatchesTerm(account, term),
        );

        return integrationAccounts && integrationAccounts.length > 0
            ? [{ ...connection, integrationAccounts }]
            : [];
    });
};
